import React, { useState } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

function CreateBankAccount() {
  const [formData, setFormData] = useState({
    account_holder_name: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    bank_branch_name: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [documentFile, setDocumentFile] = useState([]);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "ifsc_code" ? value.toUpperCase() : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    if (!formData.account_holder_name.trim()) {
      newErrors.account_holder_name = "Account Holder Name is required.";
      isValid = false;
    }
    if (!formData.bank_name.trim()) {
      newErrors.bank_name = "Bank Name is required.";
      isValid = false;
    }
    if (!formData.bank_branch_name.trim()) {
      newErrors.bank_branch_name = "Bank Branch Name is required.";
      isValid = false;
    }
    if (!/^\d+$/.test(formData.account_number)) {
      newErrors.account_number = "Account Number must be numeric.";
      isValid = false;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code)) {
      newErrors.ifsc_code = "Invalid IFSC Code format.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const data = new FormData();
      data.append("account_holder_name", formData.account_holder_name);
      data.append("bank_name", formData.bank_name);
      data.append("account_number", formData.account_number);
      data.append("ifsc_code", formData.ifsc_code);
      data.append("bank_branch_name", formData.bank_branch_name);

      documentFile.forEach((file) => {
        data.append("document", file);
      });

      const response = await fetch(`${API_URL}/create-bank-account`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      const result = await response.json();
      if (!response.ok || result.status === false) {
        Swal.fire(
          "Error",
          result.message || "Failed to create account",
          "error"
        );
        return;
      }

      Swal.fire(
        "Success",
        result.message || "Account created successfully!",
        "success"
      ).then(() => {
        navigate("/account-list");
      });

      setFormData({
        account_holder_name: "",
        bank_name: "",
        account_number: "",
        ifsc_code: "",
        bank_branch_name: "",
      });
      setDocumentFile([]);
    } catch (err) {
      Swal.fire("Error", err.message || "Unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════ INLINE STYLES ═══════════
  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  const styles = {
    page: {
      padding: "20px",
      background: "#f1f5f9",
      minHeight: "100vh",
      fontFamily: '"Poppins", "Inter", sans-serif',
    },
    card: {
      maxWidth: "1000px",
      margin: "0 auto",
      background: "#ffffff",
      borderRadius: "16px",
      boxShadow:
        "0 12px 32px -10px rgba(30, 27, 75, 0.18), 0 4px 12px -4px rgba(30, 27, 75, 0.08)",
      overflow: "hidden",
    },
    header: {
      background: gradientBg,
      padding: "20px 28px",
      color: "#ffffff",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    headerIcon: {
      width: "42px",
      height: "42px",
      borderRadius: "10px",
      background: "rgba(255, 255, 255, 0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      backdropFilter: "blur(6px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
    headerTitle: {
      margin: 0,
      fontSize: "20px",
      fontWeight: 700,
      color: "#ffffff",
      letterSpacing: "0.01em",
    },
    body: {
      padding: "28px",
    },
    row: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "20px",
      marginBottom: "20px",
    },
    rowFull: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "20px",
      marginBottom: "20px",
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      minWidth: 0,
    },
    label: {
      fontSize: "12.5px",
      fontWeight: 700,
      color: "#334155",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    required: {
      color: "#ef4444",
      fontSize: "14px",
      lineHeight: 1,
    },
    input: {
      width: "100%",
      height: "46px",
      padding: "0 14px",
      fontSize: "14px",
      fontWeight: 500,
      color: "#0f172a",
      background: "#ffffff",
      border: "1.5px solid #e2e8f0",
      borderRadius: "10px",
      outline: "none",
      boxSizing: "border-box",
      transition: "all 0.2s ease",
      fontFamily: "inherit",
    },
    inputError: {
      borderColor: "#ef4444",
      background: "#fef2f2",
    },
    errorText: {
      fontSize: "12px",
      color: "#ef4444",
      fontWeight: 500,
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    fileBox: {
      padding: "24px",
      border: "2px dashed #cbd5e1",
      borderRadius: "12px",
      background: "#f8fafc",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.22s ease",
    },
    fileBoxHover: {
      borderColor: "#6366f1",
      background: "#eef2ff",
    },
    fileLabel: {
      display: "block",
      fontSize: "13.5px",
      fontWeight: 600,
      color: "#475569",
      cursor: "pointer",
      marginBottom: "8px",
    },
    fileHint: {
      fontSize: "12px",
      color: "#94a3b8",
      margin: 0,
    },
    previewGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
      marginTop: "14px",
    },
    previewItem: {
      position: "relative",
      width: "90px",
      height: "90px",
      borderRadius: "10px",
      overflow: "hidden",
      border: "1.5px solid #e2e8f0",
      background: "#fff",
    },
    previewImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
    previewPdf: {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#fee2e2",
      color: "#dc2626",
      fontWeight: 700,
      fontSize: "13px",
    },
    removeBtn: {
      position: "absolute",
      top: "4px",
      right: "4px",
      width: "22px",
      height: "22px",
      borderRadius: "50%",
      background: "#ef4444",
      color: "#fff",
      border: "none",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1,
      boxShadow: "0 2px 6px rgba(239, 68, 68, 0.5)",
    },
    previewName: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      padding: "3px 6px",
      fontSize: "10px",
      color: "#fff",
      background: "rgba(15, 23, 42, 0.7)",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textAlign: "center",
    },
    submitRow: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      marginTop: "12px",
      paddingTop: "20px",
      borderTop: "1px solid #f1f5f9",
    },
    submitBtn: {
      height: "46px",
      padding: "0 32px",
      fontSize: "14px",
      fontWeight: 700,
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
      letterSpacing: "0.02em",
    },
    submitBtnDisabled: {
      opacity: 0.6,
      cursor: "not-allowed",
      boxShadow: "none",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* ═══════════ HEADER ═══════════ */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>🏦</div>
          <h3 style={styles.headerTitle}>Add Bank Account</h3>
        </div>

        {/* ═══════════ BODY ═══════════ */}
        <div style={styles.body}>
          <form onSubmit={handleSubmit}>
            {/* Row 1: Account Holder + Bank Name */}
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>
                  Account Holder Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="account_holder_name"
                  placeholder="Enter account holder name"
                  value={formData.account_holder_name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.account_holder_name ? styles.inputError : {}),
                  }}
                  onFocus={(e) => {
                    if (!errors.account_holder_name) {
                      e.currentTarget.style.borderColor = "#6366f1";
                      e.currentTarget.style.boxShadow =
                        "0 0 0 4px rgba(99, 102, 241, 0.12)";
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.account_holder_name) {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                />
                {errors.account_holder_name && (
                  <p style={styles.errorText}>
                    ⚠ {errors.account_holder_name}
                  </p>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  Bank Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="bank_name"
                  placeholder="Enter bank name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.bank_name ? styles.inputError : {}),
                  }}
                  onFocus={(e) => {
                    if (!errors.bank_name) {
                      e.currentTarget.style.borderColor = "#6366f1";
                      e.currentTarget.style.boxShadow =
                        "0 0 0 4px rgba(99, 102, 241, 0.12)";
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.bank_name) {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                />
                {errors.bank_name && (
                  <p style={styles.errorText}>⚠ {errors.bank_name}</p>
                )}
              </div>
            </div>

            {/* Row 2: Branch + Account Number */}
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>
                  Bank Branch Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="bank_branch_name"
                  placeholder="Enter bank branch name"
                  value={formData.bank_branch_name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.bank_branch_name ? styles.inputError : {}),
                  }}
                  onFocus={(e) => {
                    if (!errors.bank_branch_name) {
                      e.currentTarget.style.borderColor = "#6366f1";
                      e.currentTarget.style.boxShadow =
                        "0 0 0 4px rgba(99, 102, 241, 0.12)";
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.bank_branch_name) {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                />
                {errors.bank_branch_name && (
                  <p style={styles.errorText}>
                    ⚠ {errors.bank_branch_name}
                  </p>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  Account Number <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="account_number"
                  placeholder="Enter account number"
                  value={formData.account_number}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) handleChange(e);
                  }}
                  style={{
                    ...styles.input,
                    ...(errors.account_number ? styles.inputError : {}),
                  }}
                  onFocus={(e) => {
                    if (!errors.account_number) {
                      e.currentTarget.style.borderColor = "#6366f1";
                      e.currentTarget.style.boxShadow =
                        "0 0 0 4px rgba(99, 102, 241, 0.12)";
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.account_number) {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                />
                {errors.account_number && (
                  <p style={styles.errorText}>⚠ {errors.account_number}</p>
                )}
              </div>
            </div>

            {/* Row 3: IFSC */}
            <div style={styles.rowFull}>
              <div style={styles.field}>
                <label style={styles.label}>
                  IFSC Code <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  placeholder="Enter IFSC code e.g. SBIN0007587"
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData({ ...formData, ifsc_code: value });
                    if (errors.ifsc_code) {
                      setErrors((prev) => ({ ...prev, ifsc_code: null }));
                    }
                  }}
                  style={{
                    ...styles.input,
                    ...(errors.ifsc_code ? styles.inputError : {}),
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                  onFocus={(e) => {
                    if (!errors.ifsc_code) {
                      e.currentTarget.style.borderColor = "#6366f1";
                      e.currentTarget.style.boxShadow =
                        "0 0 0 4px rgba(99, 102, 241, 0.12)";
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.ifsc_code) {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                />
                {errors.ifsc_code && (
                  <p style={styles.errorText}>⚠ {errors.ifsc_code}</p>
                )}
              </div>
            </div>

            {/* File Upload */}
            <div style={styles.rowFull}>
              <div style={styles.field}>
                <label style={styles.label}>
                  Upload Passbook / Cheque{" "}
                  <span style={styles.required}>*</span>
                </label>

                <div
                  style={styles.fileBox}
                  onMouseEnter={(e) =>
                    Object.assign(e.currentTarget.style, styles.fileBoxHover)
                  }
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.background = "#f8fafc";
                  }}
                >
                  <input
                    type="file"
                    id="file-upload"
                    accept="image/*,.pdf"
                    onChange={(e) =>
                      setDocumentFile(Array.from(e.target.files))
                    }
                    multiple
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="file-upload"
                    style={styles.fileLabel}
                  >
                    📎 Click to upload or drag & drop
                  </label>
                  <p style={styles.fileHint}>
                    PNG, JPG or PDF · Multiple files allowed
                  </p>
                </div>

                {documentFile?.length > 0 && (
                  <div style={styles.previewGrid}>
                    {documentFile.map((file, index) => (
                      <div key={index} style={styles.previewItem}>
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            style={styles.previewImg}
                          />
                        ) : (
                          <div style={styles.previewPdf}>PDF</div>
                        )}
                        <button
                          type="button"
                          style={styles.removeBtn}
                          onClick={() => {
                            const newFiles = documentFile.filter(
                              (_, i) => i !== index
                            );
                            setDocumentFile(newFiles);
                          }}
                          title="Remove"
                        >
                          ×
                        </button>
                        <div style={styles.previewName}>{file.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <div style={styles.submitRow}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.submitBtn,
                  ...(loading ? styles.submitBtnDisabled : {}),
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px rgba(76, 29, 149, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 14px rgba(76, 29, 149, 0.35)";
                }}
              >
                {loading ? "Creating..." : "Create Account"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateBankAccount;